import { useContext, useEffect, useState } from "react";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { assignSupportAPI, fetchSupportUsersAPI } from "../../../../utils/api/support";
import { Button, Select } from "../../../ui";

const TEXT = {
  ar: {
    selected: (n) => `تم تحديد ${n}`,
    select: "اختر المستخدم",
    assign: "تعيين",
    usersFailed: "تعذر تحميل المستخدمين.",
    assignFailed: "تعذر تعيين التذاكر. حاول مرة أخرى.",
    notAllowed: "ليست لديك صلاحية لتعيين بعض التذاكر المحددة.",
  },
  en: {
    selected: (n) => `${n} selected`,
    select: "Select user",
    assign: "Assign",
    usersFailed: "Users could not be loaded.",
    assignFailed: "The tickets could not be assigned. Please try again.",
    notAllowed: "You are not allowed to assign some of the selected tickets.",
  },
};

export default function BulkAssignBar({ selectedIds, tickets = [], onDone }) {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const t = TEXT[lang] || TEXT.en;
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("auth-token");
    fetchSupportUsersAPI(token).then((res) => {
      if (!active) return;
      // The helper resolves to undefined on failure; `users.map` used to crash.
      if (Array.isArray(res)) setUsers(res);
      else setError(t.usersFailed);
    });
    return () => {
      active = false;
    };
    // Load once when the bar opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userOptions = users.map((u) => ({
    value: u._id,
    label: u.name,
  }));

  const handleAssign = async () => {
    if (!selectedUser || selectedIds.length === 0 || assigning) return;
    // Every selected ticket must be a loaded ticket of a restaurant the user may manage;
    // one that is not stops the whole assignment before any request.
    const allowed = selectedIds.every((id) => {
      const ticket = tickets.find((item) => item._id === id);
      return Boolean(ticket) && can("support.ticket.manage", { restaurantId: ticket.storeId?.id });
    });
    if (!allowed) {
      setError(t.notAllowed);
      return;
    }

    const token = localStorage.getItem("auth-token");
    setAssigning(true);
    setError("");
    try {
      await assignSupportAPI(token, selectedIds, selectedUser);
      onDone();
    } catch (err) {
      console.error(err);
      setError(err?.message || t.assignFailed);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="bulkAssignbar" role="region" aria-label={t.assign}>
      <h4 aria-live="polite">{t.selected(selectedIds.length)}</h4>
      <Select
        placeholder={t.select}
        options={userOptions}
        value={selectedUser}
        disabled={assigning}
        onChange={(o) => setSelectedUser(o.target.value)}
      />

      <Button
        variant="primary"
        appearance="filled"
        disabled={!selectedUser || !selectedIds.length || assigning}
        loading={assigning}
        onClick={handleAssign}
      >
        {t.assign}
      </Button>
      {error && (
        <p className="support-action-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
